import { useCallback, useEffect } from 'react';

import type { UseQueryResult } from '@tanstack/react-query';

import type { GetEmailByMessageIdResponse } from 'wg-api-sdk';

import useDocument from '@/features/documents/queries/hooks/useDocument';
import type { EmailMessage } from '@/features/email/types';
import type { PartyData } from '@/features/party/types';

import useAnalyzeFlow from './useAnalyzeFlow';

export default function useAnalysis(
  email: Maybe<EmailMessage>,
  analyzedEmailQuery: UseQueryResult<GetEmailByMessageIdResponse>,
  party: PartyData,
) {
  const { data: analyzedEmail, isLoading: isAnalyzedEmailLoading } =
    analyzedEmailQuery;

  const {
    isAnalyzing,
    isReAnalyzing,
    isPollingAnalysis,
    hasAnalysisFailed,
    analyzeEmail,
    reAnalyze,
  } = useAnalyzeFlow(email, party);

  const { data: emailDocument } = useDocument(
    analyzedEmail?.email.document_id ?? '',
    { enabled: Boolean(analyzedEmail?.email.document_id) },
  );

  const forceAnalyze = useCallback(
    () => analyzeEmail({ forceAnalyze: true }),
    [analyzeEmail],
  );

  useEffect(() => {
    // Don't start flow when already running
    if (isAnalyzing) return;
    // Don't analyze when loading data
    if (isAnalyzedEmailLoading) return;
    // Don't analyze when no email
    if (!email) return;
    // Don't start when no party selected
    if (party.status !== 'Selected') return;

    // Don't analyze when we already have data, except
    // when the party id is different from what we have.
    if (analyzedEmail && analyzedEmail?.analysis?.party_id === party.party?.id)
      return;

    analyzeEmail();
  }, [
    analyzedEmail,
    isAnalyzing,
    isAnalyzedEmailLoading,
    email,
    party.status,
    analyzeEmail,
    party.party?.id,
  ]);

  return {
    isLoading: isAnalyzedEmailLoading || isAnalyzing || isReAnalyzing,
    isAnalyzing,
    isReAnalyzing,
    isPollingAnalysis,
    hasAnalysisFailed,

    email: analyzedEmail?.email,
    triage: {
      classification: analyzedEmail?.email.triage_classification,
      actionability: analyzedEmail?.email.triage_actionability,
    },
    analysis: analyzedEmail?.analysis,
    conversation: analyzedEmail?.conversation,
    conversationParties: analyzedEmail?.conversation_parties,

    relevant: {
      policyIds: analyzedEmail?.analysis?.relevant_insurance_policy_ids,
      policyPackageIds: analyzedEmail?.analysis?.relevant_insurance_package_ids,
      invoiceIds: analyzedEmail?.analysis?.relevant_invoice_ids,
      claimIds: analyzedEmail?.analysis?.relevant_claim_ids,
      riskObjectIds: analyzedEmail?.analysis?.relevant_risk_object_ids,
      noteIds: analyzedEmail?.analysis?.relevant_note_ids,
      documentIds: analyzedEmail?.analysis?.relevant_document_ids,
    },

    actions: analyzedEmail?.actions ?? [],

    emailDocument,

    forceAnalyze,
    reAnalyze,
  };
}
